/**
 * __OFF_SHIFT_BOARD__ (2026-09-25) shift state for the Fleet Map, read from shift_sessions --
 * the table Billing-Manager's sign-on / sign-off writes and the dispatch app's map is told
 * from (see src/utils/offShift.js for the rule). Read-only.
 */
import { supabase } from '../supabaseClient';
import { shiftRoster, isOffShift, truckDriverId, naiveUtc, tsMs, offShiftLocation } from '../utils/offShift';

const CLOSED_LOOKBACK_DAYS = 21;

/** { shifts: {driverId: {on, since}}, drivers: [...] }, or null when it could not be read. */
export const fetchShiftStates = async () => {
  try {
    const { data: drivers, error } = await supabase
      .from('drivers')
      .select('id, name, user_id, current_truck_id')
      .not('user_id', 'is', null)
      .limit(1000);
    if (error) throw error;
    const uids = [...new Set((drivers || []).map((d) => d.user_id).filter(Boolean).map(String))];
    const since = new Date(Date.now() - CLOSED_LOOKBACK_DAYS * 86400000).toISOString();
    let open = [];
    let closed = [];
    for (let i = 0; i < uids.length; i += 100) {
      const chunk = uids.slice(i, i + 100);
      const o = await supabase.from('shift_sessions')
        .select('user_id, signed_in_at, signed_out_at')
        .in('user_id', chunk).is('signed_out_at', null).limit(1000);
      if (o.error) throw o.error;
      const c = await supabase.from('shift_sessions')
        .select('user_id, signed_out_at')
        .in('user_id', chunk).not('signed_out_at', 'is', null).gte('signed_out_at', since)
        .order('signed_out_at', { ascending: false }).limit(2000);
      if (c.error) throw c.error;
      open = open.concat(o.data || []);
      closed = closed.concat(c.data || []);
    }
    return { shifts: shiftRoster(drivers || [], open, closed), drivers: drivers || [] };
  } catch (e) {
    console.error('Shift state fetch error:', e);
    return null;
  }
};

/** This driver's last position in this truck at or before the sign-off (null if none / unreadable). */
const lastPingBefore = async (truckId, driverId, sinceMs) => {
  let q = supabase.from('gps_locations')
    .select('truck_id, driver_id, latitude, longitude, timestamp')
    .eq('truck_id', truckId).eq('driver_id', driverId);
  if (sinceMs != null) q = q.lte('timestamp', naiveUtc(sinceMs));
  const { data, error } = await q.order('timestamp', { ascending: false }).limit(1);
  if (error) throw error;
  return (data && data[0]) || null;
};

/**
 * The GPS rows (fetchRealGpsLocations) with every off-shift driver's truck replaced by his
 * grey, last-known-before-sign-off row. st null (shift data unreadable) = rows as before.
 * A failed position lookup shows NO position rather than a newer one.
 */
export const applyOffShift = async (trucks, locations, st, nowMs = Date.now()) => {
  if (!st || !st.shifts) return locations;
  const out = { ...(locations || {}) };
  const names = {};
  (st.drivers || []).forEach((d) => { names[String(d.id)] = d.name || ''; });
  await Promise.all((trucks || []).map(async (t) => {
    const loc = (locations || {})[t.id];
    const did = truckDriverId(t.id, loc && loc.driverId, st.drivers);
    if (!did || !isOffShift(st.shifts, did)) return;
    const info = st.shifts[did];
    let ping = null;
    try { ping = await lastPingBefore(t.id, did, tsMs(info.since)); } catch (e) { ping = null; }
    out[t.id] = offShiftLocation(
      loc || { truckId: t.id, truckNumber: t.truck_number, licensePlate: t.license_plate },
      ping, info, names[did], nowMs);
  }));
  return out;
};
